import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { EChartsOption } from 'echarts';
import { RapportsService, FiltreRapport } from '../rapports.service';
import { FiltresCommunsRapports } from '../rapports.component';

@Component({
  selector: 'app-rapport-volumes',
  templateUrl: './rapport-volumes.component.html'
})
export class RapportVolumesComponent implements OnInit, OnChanges {
  @Input() filtres!: FiltresCommunsRapports;

  chargement = {
    total: true, parType: true, pics: true, evolution: true,
    delais: true, recl: true, fiabilite: true, erreursPart: true
  };

  total: any = null;
  parType: any = null;
  pics: any = null;
  evolution: any = null;
  delais: any = null;
  recl: any = null;
  fiabilite: any = null;
  erreursPart: any = null;

  typePartenaire: 'LIVREUR' | 'CENTRE_TRI_DEPART' | 'CENTRE_TRI_ARRIVEE' = 'LIVREUR';

  optTotal: EChartsOption = {};
  optParType: EChartsOption = {};
  optHeatmap: EChartsOption = {};
  optEvolution: EChartsOption = {};
  optDelais: EChartsOption = {};
  optRecl: EChartsOption = {};

  constructor(private service: RapportsService) {}

  ngOnInit(): void { this.charger(); }
  ngOnChanges(): void { this.charger(); }

  private filtreApi(extra: Partial<FiltreRapport> = {}): FiltreRapport {
    return {
      debut: this.filtres.debut, fin: this.filtres.fin,
      zoneId: this.filtres.zoneId, typeEnvoi: this.filtres.typeEnvoi,
      granularite: this.filtres.granularite,
      ...extra
    };
  }

  charger(): void {
    this.chargerTotal();
    this.chargerParType();
    this.chargerPics();
    this.chargerEvolution();
    this.chargerDelais();
    this.chargerRecl();
    this.chargerFiabilite();
    this.chargerErreursPart();
  }

  private chargerTotal(): void {
    this.chargement.total = true;
    this.service.volumeTotal(this.filtreApi()).subscribe({
      next: (d) => {
        this.total = d;
        const serie = d.serie || {};
        const keys = Object.keys(serie);
        this.optTotal = {
          tooltip: { trigger: 'axis' },
          grid: { left: 40, right: 20, top: 30, bottom: 30 },
          xAxis: { type: 'category', data: keys, axisLabel: { color: '#64748b', fontSize: 11 } },
          yAxis: { type: 'value', splitLine: { lineStyle: { color: '#f1f5f9' } }, axisLabel: { color: '#64748b' } },
          series: [{
            name: 'Envois', type: 'bar',
            data: keys.map(k => serie[k]),
            itemStyle: { color: '#0ea5e9', borderRadius: [4, 4, 0, 0] }, barWidth: '60%'
          }]
        };
        this.chargement.total = false;
      },
      error: () => this.chargement.total = false
    });
  }

  private chargerParType(): void {
    this.chargement.parType = true;
    this.service.volumeParType(this.filtreApi()).subscribe({
      next: (d) => {
        this.parType = d;
        const map = d.repartition || {};
        const labels: any = {
          EXPRESS_URBAIN: 'Express urbain',
          EXPRESS_INTERURBAIN: 'Express interurbain',
          STANDARD_URBAIN: 'Standard urbain',
          STANDARD_INTERURBAIN: 'Standard interurbain'
        };
        this.optParType = {
          tooltip: { trigger: 'item' },
          legend: { bottom: 0, type: 'scroll' },
          series: [{
            type: 'pie', radius: ['45%', '70%'],
            itemStyle: { borderRadius: 6, borderColor: '#fff', borderWidth: 2 },
            label: { show: false }, labelLine: { show: false },
            data: Object.entries(map).map(([k, v]) => ({ name: labels[k] || k, value: v as number }))
          }]
        };
        this.chargement.parType = false;
      },
      error: () => this.chargement.parType = false
    });
  }

  private chargerPics(): void {
    this.chargement.pics = true;
    this.service.picsVolumes(this.filtreApi()).subscribe({
      next: (d) => {
        this.pics = d;
        const jours = d.joursLabels || ['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'];
        const heures: string[] = [];
        for (let h = 0; h < 24; h++) heures.push(String(h) + 'h');
        const data = (d.heatmap || []).map((row: number[]) => [row[1], row[0], row[2]]);
        const maxVal = Math.max(1, ...data.map((r: any) => r[2]));
        this.optHeatmap = {
          tooltip: { position: 'top' },
          grid: { left: 50, right: 20, top: 20, bottom: 20 },
          xAxis: { type: 'category', data: heures, splitArea: { show: true }, axisLabel: { color: '#64748b', fontSize: 10 } },
          yAxis: { type: 'category', data: jours, splitArea: { show: true }, axisLabel: { color: '#64748b' } },
          visualMap: {
            min: 0, max: maxVal,
            calculable: true, orient: 'horizontal', left: 'center', bottom: '0%',
            inRange: { color: ['#e0f2fe', '#0284c7', '#0c4a6e'] }
          },
          series: [{
            name: 'Envois', type: 'heatmap', data,
            label: { show: false },
            emphasis: { itemStyle: { shadowBlur: 8, shadowColor: 'rgba(0,0,0,0.3)' } }
          }]
        };
        this.chargement.pics = false;
      },
      error: () => this.chargement.pics = false
    });
  }

  private chargerEvolution(): void {
    this.chargement.evolution = true;
    this.service.evolutionVolumes(this.filtreApi()).subscribe({
      next: (d) => {
        this.evolution = d;
        const actuel = d.serieActuelle || {};
        const n1 = d.serieN1 || {};
        const keys = Array.from(new Set([...Object.keys(actuel), ...Object.keys(n1)])).sort();
        this.optEvolution = {
          tooltip: { trigger: 'axis' },
          legend: { top: 0, data: ['Période actuelle', 'N-1'] },
          grid: { left: 40, right: 20, top: 30, bottom: 30 },
          xAxis: { type: 'category', data: keys, axisLabel: { color: '#64748b', fontSize: 11 } },
          yAxis: { type: 'value', splitLine: { lineStyle: { color: '#f1f5f9' } }, axisLabel: { color: '#64748b' } },
          series: [
            { name: 'Période actuelle', type: 'line', smooth: true, data: keys.map(k => actuel[k] || 0), lineStyle: { color: '#10b981', width: 2.5 }, itemStyle: { color: '#10b981' } },
            { name: 'N-1', type: 'line', smooth: true, data: keys.map(k => n1[k] || 0), lineStyle: { color: '#94a3b8', width: 2, type: 'dashed' }, itemStyle: { color: '#94a3b8' } }
          ]
        };
        this.chargement.evolution = false;
      },
      error: () => this.chargement.evolution = false
    });
  }

  private chargerDelais(): void {
    this.chargement.delais = true;
    this.service.evolutionDelais(this.filtreApi()).subscribe({
      next: (d) => {
        this.delais = d;
        const ev = d.evolutionMensuelle || {};
        const keys = Object.keys(ev);
        this.optDelais = {
          tooltip: { trigger: 'axis', formatter: (p: any) => p[0].axisValue + ' : ' + p[0].data + ' h' },
          grid: { left: 40, right: 20, top: 30, bottom: 30 },
          xAxis: { type: 'category', data: keys, axisLabel: { color: '#64748b', fontSize: 11 } },
          yAxis: { type: 'value', splitLine: { lineStyle: { color: '#f1f5f9' } }, axisLabel: { color: '#64748b', formatter: '{value} h' } },
          series: [{
            name: 'Délai moyen', type: 'line', smooth: true,
            data: keys.map(k => ev[k]),
            lineStyle: { color: '#6366f1', width: 2.5 },
            itemStyle: { color: '#6366f1' },
            areaStyle: { color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [
              { offset: 0, color: 'rgba(99,102,241,0.25)' }, { offset: 1, color: 'rgba(99,102,241,0)' }
            ]}}
          }]
        };
        this.chargement.delais = false;
      },
      error: () => this.chargement.delais = false
    });
  }

  private chargerRecl(): void {
    this.chargement.recl = true;
    this.service.evolutionReclamations(this.filtreApi()).subscribe({
      next: (d) => {
        this.recl = d;
        const serie = d.serieMensuelle || {};
        const keys = Object.keys(serie);
        this.optRecl = {
          tooltip: { trigger: 'axis' },
          grid: { left: 40, right: 20, top: 30, bottom: 30 },
          xAxis: { type: 'category', data: keys, axisLabel: { color: '#64748b', fontSize: 11 } },
          yAxis: { type: 'value', splitLine: { lineStyle: { color: '#f1f5f9' } }, axisLabel: { color: '#64748b' } },
          series: [{
            name: 'Réclamations', type: 'line', smooth: true,
            data: keys.map(k => serie[k]),
            lineStyle: { color: '#f59e0b', width: 2.5 },
            itemStyle: { color: '#f59e0b' },
            areaStyle: { color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [
              { offset: 0, color: 'rgba(245,158,11,0.25)' }, { offset: 1, color: 'rgba(245,158,11,0)' }
            ]}}
          }]
        };
        this.chargement.recl = false;
      },
      error: () => this.chargement.recl = false
    });
  }

  private chargerFiabilite(): void {
    this.chargement.fiabilite = true;
    this.service.fiabilitePartenaires(this.filtreApi({ typePartenaire: this.typePartenaire })).subscribe({
      next: (d) => { this.fiabilite = d; this.chargement.fiabilite = false; },
      error: () => this.chargement.fiabilite = false
    });
  }

  private chargerErreursPart(): void {
    this.chargement.erreursPart = true;
    this.service.erreursPartenaires(this.filtreApi({ typePartenaire: this.typePartenaire })).subscribe({
      next: (d) => { this.erreursPart = d; this.chargement.erreursPart = false; },
      error: () => this.chargement.erreursPart = false
    });
  }

  rechargerPartenaires(): void {
    this.chargerFiabilite();
    this.chargerErreursPart();
  }
}
